/**
 * The D1 curated activity set — which `session.tool` frames become visible
 * work in the transcript, what they are called in the lawyer's language, and
 * how a frame turns into a renderable block.
 *
 * One module for all three so the set, the labels and the parse cannot drift:
 * adding a tool to `CURATED_TOOLS` without a label would render its raw name,
 * and a label without the set entry would never fire.
 *
 * ## Why a lexide-side constant is the right shape here
 *
 * The dossier rule "server-owned vocabularies must be DECLARED, never inferred"
 * does not bite: this set does not claim to know which tools EXIST, it selects
 * which of the frames the server already sends get RENDERED. That is a UX
 * curation. (Agreed with `concierge` on thread `…-24vl`, open question 1.)
 *
 * ## Wire shape — measured, not assumed (2026-08-13)
 *
 * `session.tool` frames carry pi-native `data.name` / `data.args`; the
 * `toolName` / `toolInput` spelling is a rekeyed variant that this wire never
 * actually sends (`pi-embedded-subscribe.handlers.tools.ts` :651 start, :761
 * update, :936 result). Both are tolerated here anyway — the cost is one `??`
 * and the alternative failure mode is silence.
 *
 * `phase` vocabulary is `start` | `update` | `result` (NOT "end").
 * `result` / `partialResult` are deleted server-side unless the session sets
 * `verbose=full` (`server-chat.ts:612-622`), which is what makes D5
 * ("start/args only, never result payloads") an invariant here rather than a
 * discipline this file has to maintain. `isError` DOES survive that strip, so
 * a completion outcome is measured, never guessed.
 */

import type { ToolUseBlock } from "@/types/content-blocks";
import type { ToolTrailEntry } from "@/types/gateway";
import { isSystemPath } from "@/lib/chat/system-path";

/**
 * D1 (ratified): the file/search/edit/browser families.
 *
 * ⚠ Mission tools (`lx_delegate`, `lx_mission_status`) are DELIBERATELY absent —
 * async-agent progress renders only on the activity sidebar, never in chat
 * (operator ruling 2026-07-18, re-confirmed as D4). The plan tools stay because
 * they are how the TodoCard already reaches the transcript.
 */
export const CURATED_TOOLS = [
  // plan tools — the pre-existing bridge, kept
  "lx_todo",
  "update_plan",
  // read / list / search
  "lx_read",
  "lx_list",
  "lx_search",
  // write / edit
  "lx_write",
  "lx_edit",
  "lx_doc_write",
  "lx_doc_patch",
  // workspace
  "lx_open_file",
  "lx_move_resource",
  // native browser (the "navigate" action has its own renderer)
  "browser",
  // help (operator directive 2026-08-14 — it fires on real turns and its row
  // must be presentable; registration: lextools lx-help.ts, HOT bundle)
  "lx_help",
  // web tools (S7, ai-docs/lexide-web-tools-activity-miniplan.md) — names
  // verified against the REGISTRATIONS, not the plan prose (the lx_copy/lx_move
  // scar): web-search.ts:84 and web-fetch.ts:639. Both upstream-core, so the
  // names hold regardless of which provider plugin serves them. Inert until the
  // S3/S4 roll makes the tools exist; live the moment it does.
  "web_search",
  "web_fetch",
  // A-11 re-probe finding (2026-08-15): the tool fires on real turns (it is
  // the concierge's PREFERRED web door since browser-first) but was invisible
  // live — a 13s turn rendered zero rows, and the turn-end provider badge had
  // no block to anchor on, making "via navegador" reload-only. Name verified
  // against the registration (LX_BROWSER_FETCH_TOOL_NAME, lx-browser-fetch.ts:32),
  // arg is `url` (required, :234).
  "lx_browser_fetch",
] as const;
// ⚠ Every name above was checked against the runtime's own registration
// (`extensions/lexiun-core/openclaw.plugin.json`) on 2026-08-13, because the
// first draft of this list did NOT do that and got two entries wrong: it
// carried `lx_copy` and `lx_move`, which do not exist — they were read off D1's
// prose ("copy/move") rather than the registry, where the tool is actually
// `lx_move_resource`. A curated entry for a tool that never fires is invisible:
// it looks like coverage and behaves like a gap.
//
// `lx_list` is here because the DEV tally below caught it — 10 calls in a
// single measured turn, dropped on the floor by the first draft. That is the
// tally doing exactly the job it exists for, and it is why the set is allowed
// to be a local constant at all: it widens from evidence.

const CURATED = new Set<string>(CURATED_TOOLS);

/** Tools that must NEVER render in chat regardless of any future widening of
 *  the set above. Kept as an explicit denylist rather than "absent from
 *  CURATED" so a careless addition cannot quietly revoke the 2026-07-18
 *  ruling. */
const NEVER_IN_CHAT = new Set<string>(["lx_delegate", "lx_mission_status"]);

export function isCuratedTool(name: string): boolean {
  return CURATED.has(name) && !NEVER_IN_CHAT.has(name);
}

/**
 * DEV-only tally of tool names that arrived and were NOT rendered.
 *
 * "Curated" and "silently dropped" look identical from outside the app, which
 * is precisely how the 2-name whitelist stayed unnoticed for months while every
 * other tool frame hit the floor. This makes the discarded set queryable
 * (`window.__toolActivityUnrendered` in devtools) so the curation widens from
 * MEASUREMENT rather than from someone remembering a tool exists.
 *
 * Mirrors `__chatDropped` in `useChatSession.ts`; tree-shaken out of prod
 * builds (every call site is behind `import.meta.env.DEV`).
 */
function recordUnrenderedTool(name: string): void {
  // Guarded because this parse also runs in the node-env unit suite, where
  // there is no `window` — a diagnostic that CRASHES the thing it observes is
  // worse than no diagnostic.
  if (typeof window === "undefined") return;
  const w = window as unknown as { __toolActivityUnrendered?: Record<string, number> };
  w.__toolActivityUnrendered ??= {};
  w.__toolActivityUnrendered[name] = (w.__toolActivityUnrendered[name] ?? 0) + 1;
}

/** The `data` half of a `session.tool` frame, both spellings tolerated. */
interface ToolFrameData {
  phase?: unknown;
  name?: unknown;
  args?: unknown;
  toolName?: unknown;
  toolInput?: unknown;
  toolCallId?: unknown;
  isError?: unknown;
}

interface ToolFrame {
  sessionKey?: unknown;
  runId?: unknown;
  data?: ToolFrameData;
}

/** A parsed activity frame — what the bridge needs, with the wire's two
 *  spellings already reconciled. */
export interface ToolActivityFrame {
  phase: "start" | "update" | "result";
  name: string;
  args: Record<string, unknown>;
  /** Stable per-invocation id; falls back to name+runId when the wire omits
   *  `toolCallId` (older frames), which keeps upsert-by-id working. */
  callId: string;
  /** Only meaningful on `phase: "result"`. `true` = the tool failed. */
  isError: boolean;
}

/**
 * Parse a raw `session.tool` payload addressed to `sessionKey` into an
 * activity frame, or `null` when it is not ours / not curated / malformed.
 *
 * Returns `null` for excluded tools rather than throwing: an uncurated tool is
 * a normal outcome, not an error. DEV tallies it (see above).
 */
export function parseToolActivityFrame(
  payload: unknown,
  sessionKey: string,
): ToolActivityFrame | null {
  const p = payload as ToolFrame;
  if (p?.sessionKey !== sessionKey) return null;
  const d = p.data;
  if (!d) return null;

  const phase = d.phase;
  if (phase !== "start" && phase !== "update" && phase !== "result") return null;

  const rawName = d.name ?? d.toolName;
  if (typeof rawName !== "string" || rawName.length === 0) return null;

  if (!isCuratedTool(rawName)) {
    if (import.meta.env.DEV) recordUnrenderedTool(rawName);
    return null;
  }

  const rawArgs = d.args ?? d.toolInput;
  const args =
    rawArgs && typeof rawArgs === "object" ? (rawArgs as Record<string, unknown>) : {};

  const callId =
    typeof d.toolCallId === "string" && d.toolCallId
      ? d.toolCallId
      : `${rawName}:${typeof p.runId === "string" ? p.runId : "live"}`;

  return { phase, name: rawName, args, callId, isError: d.isError === true };
}

/** Build the renderable block for a curated frame. The existing
 *  `ToolUseBlock` dispatcher renders it (TodoCard / ReadBadge / EditBlock /
 *  WriteBlock / OpenedFileBadge / BrowserNavigateBlock / CollapsedToolJson). */
/**
 * The name a step carries once its target has been recognised as System tier.
 *
 * Renaming at INGEST is what makes the gate structural: the real path never
 * enters the block, so `ReadBadge`/`WriteBlock`/`EditBlock`/`OpenedFileBadge`/
 * `ListBadge` — and any renderer added later — cannot print it even by mistake.
 * A gate living inside those renderers would have to be remembered six times and
 * once more for every future one. See `lib/chat/system-path.ts` (DS-37).
 */
export const SYSTEM_STEP_NAME = "__lx_system_step__";

export function toolBlockFromActivityFrame(frame: ToolActivityFrame): ToolUseBlock {
  // ⚠ System-tier steps are stripped HERE, at ingest — args and all. The block
  // keeps its identity (so the step still appears; the lawyer sees that work
  // happened) but carries nothing that could name the file.
  if (isSystemPathInArgs(frame.args)) {
    return { type: "tool_use", id: frame.callId, name: SYSTEM_STEP_NAME, input: {} };
  }
  return { type: "tool_use", id: frame.callId, name: frame.name, input: frame.args };
}

/**
 * Does any path-ish argument point at the System tier?
 *
 * Checked over the ARGS rather than over `activityTarget`'s summary, because
 * that summary is lossy by design (for `lx_search` it is the query, not a path;
 * for others it is a basename). Relying on it would let a step whose summary is
 * not a path carry a system path in its args straight through to a renderer that
 * reads `input.path` — which is exactly what `ReadBadge` does.
 */
function isSystemPathInArgs(args: Record<string, unknown>): boolean {
  for (const k of ["path", "file", "target", "to", "from", "dir", "folder"]) {
    const v = args[k];
    if (typeof v === "string" && isSystemPath(v)) return true;
  }
  return false;
}

/**
 * The single most useful field of a tool's arguments — the thing the lawyer
 * would name if they described the step out loud ("Lendo *a petição*").
 *
 * Truncated at TARGET_MAX_CHARS: this reaches the status LINE, and an
 * untruncated path from a deep workspace tree would wrap the header and change
 * the tail height on every frame, which is exactly the growth the scroll design
 * (miniplan §2) is keeping bounded.
 */
const TARGET_MAX_CHARS = 60;

export function activityTarget(name: string, args: Record<string, unknown>): string | undefined {
  const pick = (...keys: string[]): string | undefined => {
    for (const k of keys) {
      const v = args[k];
      if (typeof v === "string" && v.trim()) return v.trim();
    }
    return undefined;
  };

  const raw =
    name === "browser" || name === "web_fetch" || name === "lx_browser_fetch"
      ? pick("url")
      : name === "lx_search" || name === "web_search"
        ? pick("query", "q", "text")
        : pick("path", "file", "target", "to", "name", "title");
  if (!raw) return undefined;
  // The System tier has no lawyer-facing name — not even a basename. This helper
  // is not rendered today (the status header was retired 2026-08-13) and is
  // gated anyway: it is precisely the sort of field that gets revived without
  // anyone re-auditing what it may disclose.
  if (isSystemPath(raw)) return undefined;

  // Show the basename for paths — a lawyer recognises "peticao.pdf", not
  // "sources/processo/2024/peticao.pdf". Search queries and URLs keep their
  // full text: a URL's basename is MISLEADING, not shorter ("sumulas" out of
  // "https://tst.jus.br/sumulas" reads like a workspace file). Caught by S7's
  // own unit test before it could render anywhere.
  const keepWhole =
    name === "lx_search" ||
    name === "web_search" ||
    name === "browser" ||
    name === "web_fetch" ||
    name === "lx_browser_fetch";
  const shown = keepWhole ? raw : (raw.split("/").pop() ?? raw);
  return shown.length > TARGET_MAX_CHARS ? `${shown.slice(0, TARGET_MAX_CHARS - 1)}…` : shown;
}

/**
 * i18n key for a tool's friendly activity verb, e.g.
 * `chat.activity.tool.lx_read` → "Lendo {{target}}…".
 *
 * Falls back to a generic "working" key rather than the raw tool name: a
 * curated tool with a missing label is a translation gap, and showing
 * `lx_doc_patch…` to a lawyer is worse than showing "Trabalhando…".
 */
export function activityLabelKey(name: string): string {
  return `chat.activity.tool.${name}`;
}

export const ACTIVITY_FALLBACK_KEY = "chat.activity.working";
export const ACTIVITY_IDLE_KEY = "chat.activity.thinking";

/**
 * Split a frozen duration into the parts the header reads out:
 * "12 s", "1 min 4 s", "2 min" (seconds dropped on a clean minute).
 *
 * ⚠ Floors at 1 s for any measured duration. A turn that freezes in under half
 * a second would otherwise round to "Trabalhou por 0 s" — the exact string the
 * omit rule keeps off screen, arriving from a REAL measurement rather than a
 * missing one. A sub-second turn did work; "0 s" says it did none. True absence
 * renders no duration at all and never reaches this function.
 */
export function formatWorked(ms: number): { min: number; sec: number } {
  const total = Math.max(1, Math.round(ms / 1000));
  return { min: Math.floor(total / 60), sec: total % 60 };
}

/**
 * The plan tools — the ONE family whose live block mutates in place instead of
 * appending per invocation.
 *
 * `lx_todo`/`update_plan` re-emit the whole evolving checklist on every call, so
 * one TodoCard that updates is the correct render and N snapshots of the same
 * list is not. Every other tool appends a row, because each invocation is a
 * distinct step in the history the lawyer is watching accumulate.
 */
export function isPlanTool(name: string): boolean {
  return name === "lx_todo" || name === "update_plan";
}

/**
 * ⚠ `activityTarget` + the store's `activity.latest` are NOT RENDERED today.
 *
 * They fed the live status HEADER, which the operator retired on 2026-08-13 in
 * favour of a flat sequential log (see `TurnActivity`'s docblock): once each
 * step prints its own row, a header naming the current tool is a second copy of
 * the last row. The bridge still computes `latest` because the same store action
 * re-arms the `workedMs` freeze, and `activityTarget` still truncates targets
 * for it.
 *
 * They are kept, not orphaned by accident: if the trailing "Pensando…" cue is
 * ever asked to name the running tool, this is the substrate. If you are reading
 * this because you are removing dead code — check that ask first, then delete
 * freely.
 */

/**
 * How each tool PRESENTS to the lawyer — the label key and whether its target
 * renders. One table for both, because the operator's finding was both at once:
 * a row reading "✓ lx_help <the lawyer's entire question>" is a raw identifier
 * AND a target that should never have rendered, sharing one line.
 *
 * ## 🚨 The last resort is a PRESENTABLE GENERIC, never the identifier
 *
 * Operator directive 2026-08-14, overruling this lane's own Item-C clause
 * ("an unmapped tool still shows its raw name"): internal tool identifiers are
 * internals — the same class as the ENOENT paths (DS-37's sibling ruling; the
 * two together are one doctrine: internals never render). The generic keeps
 * Item C's real point — nothing is INVENTED per-tool — while dropping what it
 * got wrong, that `lx_help` is acceptable prose.
 *
 * The DEV tally (`__toolActivityUnrendered`) still surfaces unmapped names, so
 * the generic cannot make curation gaps invisible to us.
 *
 * ## Target policy
 *
 * file-ish → the path IS the step's subject (system paths already stripped at
 * ingest, DS-37); search-ish → the short query ("Busca FGTS"); lx_help and
 * UNKNOWN → nothing: lx_help's arg is the lawyer's own question (`query`,
 * lextools lx-help.ts:40) and rendering it duplicated their message verbatim
 * one line under their own bubble.
 */
const TOOL_PRESENTATION: Record<string, { showTarget: boolean }> = {
  lx_read: { showTarget: true },
  lx_list: { showTarget: true },
  lx_write: { showTarget: true },
  lx_edit: { showTarget: true },
  lx_doc_write: { showTarget: true },
  lx_doc_patch: { showTarget: true },
  lx_open_file: { showTarget: true },
  lx_move_resource: { showTarget: true },
  lx_search: { showTarget: true },
  web_search: { showTarget: true },
  web_fetch: { showTarget: true },
  browser: { showTarget: true },
  lx_browser_fetch: { showTarget: true },
  lx_todo: { showTarget: false },
  update_plan: { showTarget: false },
  lx_help: { showTarget: false },
};

/** Does this tool's target render? Unknown tools: NO — we cannot know whether
 *  their argument is a path or a person's own words, and the safe default is
 *  the one that cannot duplicate a message. */
export function showsTarget(name: string): boolean {
  return TOOL_PRESENTATION[name]?.showTarget ?? false;
}

/** The i18n label-key chain for a tool: its own noun, else the generic. The
 *  caller passes both through `t`; the RAW NAME is never a rendering fallback. */
export const GENERIC_TOOL_LABEL_KEY = "chat.activity.trailLabel.generic";

/**
 * The ONE-LINE chip-class steps — everything the checklist unification renders
 * as a flat trail row LIVE (operator, 2026-08-14: "the previous items used to
 * display as check list"). Rich renderers are deliberately NOT here: the
 * TodoCard, EditBlock's diff and WriteBlock's content carry payloads a flat
 * row cannot, and flattening them is an open ruling, not a default.
 */
const CHECKLIST_TOOLS = new Set<string>([
  "lx_read",
  "lx_list",
  "lx_search",
  "web_search",
  "web_fetch",
  "lx_open_file",
  "lx_move_resource",
  "lx_help",
  "browser",
  "lx_browser_fetch",
  SYSTEM_STEP_NAME,
]);

export function isChecklistTool(name: string): boolean {
  return CHECKLIST_TOOLS.has(name);
}

/**
 * Strip a persisted trail entry that names the System tier.
 *
 * Marked rather than dropped: the step HAPPENED and the lawyer is entitled to
 * see that work occurred — what they were promised they would not see is WHICH
 * internal file it touched (`case-workspace-guide.md` §2.3). So the row stays,
 * the target goes, and `TrailRow` renders the one fixed phrase.
 *
 * ⚠ Spreads `...rest` deliberately: every OTHER field survives — including
 * fields this function predates (A-11's `provider`/`path`). A System-tier row
 * keeping its provider is correct: what DS-37 hides is WHICH file, not whether
 * an external service was involved. Pinned by a unit test; do not rewrite as
 * an allowlist of known keys.
 */
export function redactSystemTrailEntry(entry: ToolTrailEntry): ToolTrailEntry {
  if (!isSystemPath(entry.target)) return entry;
  const { target: _dropped, label: _label, ...rest } = entry;
  return { ...rest, name: SYSTEM_STEP_NAME };
}

/**
 * ⚖ A-11/A-14 — the ONE display-label mapping for provider ids, shared by the
 * TrailRow badge AND the Detalhes pane's provenance rows so the two surfaces
 * cannot drift. Provider ids are an OPEN vocabulary: a known id gets its
 * locale label (`chat.activity.providerLabel.<id>`), an UNKNOWN id renders
 * VERBATIM — never a generic word (identity is the point of A-11) and never a
 * plausible substitute (an attribution surface must not lie confidently).
 * The `"http"` SENTINEL ("no provider involved — fetched directly") is the
 * caller's branch, not this helper's: it must render the direct-access
 * wording, so passing it here would be a bug.
 */
export function providerDisplayLabel(
  t: (key: string, opts?: { defaultValue: string }) => string,
  providerId: string,
): string {
  return t(`chat.activity.providerLabel.${providerId}`, { defaultValue: providerId });
}
